import React, { useState, useRef, useEffect } from 'react';
import styles from './heroVideo.module.css';

interface HeroVideoProps {
  videoUrl?: string;
  posterUrl?: string;
  autoplay?: boolean;
}

const HeroVideo: React.FC<HeroVideoProps> = ({ 
  // videoUrl = 'https://static-perf1.zupee.com/blog-images/uploads/2025/11/want-non-stop-drama-subscription.mp4',
  videoUrl,
  posterUrl,
  autoplay = true
}) => {
  const [isMuted, setIsMuted] = useState(true);
  const [showPoster, setShowPoster] = useState(!autoplay);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Auto-play video on mount if autoplay is enabled
  useEffect(() => {
    if (autoplay && videoRef.current && videoUrl) {
      // Hide poster immediately to show video
      setShowPoster(false);
      
      // Attempt to play the video
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Autoplay started successfully
            console.log('Video autoplay started');
          })
          .catch((error) => {
            // Autoplay was prevented by browser
            console.log('Video autoplay prevented:', error);
            // Show poster so user can manually start video
            setShowPoster(true);
          });
      }
    }
  }, [autoplay, videoUrl]);

  const handleAudioToggle = () => {
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  };

  const handlePosterClick = () => {
    if (videoRef.current) {
      setShowPoster(false);
      
      // Play the video and handle the promise
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video started playing successfully
            console.log('Video play started successfully');
          })
          .catch((error) => {
            // Video play was prevented or failed
            console.log('Error playing video:', error);
            // Show poster again if play fails
            setShowPoster(true);
          });
      }
    }
  };

  const handleStopClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setShowPoster(true);
    }
  };

  return (
    <div className={styles.heroSection}>
      <div className={styles.logoContainer}>
        <img 
          src="https://static-perf1.zupee.com/blog-images/uploads/2025/11/vertical-tv-logo1.png" 
          alt="Vertical by Zupee" 
          className={styles.logoImage}
        />
      </div>
      <button 
        className={styles.audioIcon}
        onClick={handleAudioToggle}
        aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
      >
        {!isMuted ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="#ffffff"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="#ffffff"/>
          </svg>
        )}
      </button>
      <div className={styles.heroContent}>
        <div className={styles.heroVideoContainer}>
          {videoUrl && (
            <>
              <video
                ref={videoRef}
                className={styles.videoElement}
                autoPlay={autoplay}
                loop
                muted={isMuted}
                playsInline
                poster={posterUrl}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
              {showPoster && (
                <div 
                  className={styles.videoPoster}
                  onClick={handlePosterClick}
                  style={{ cursor: 'pointer' }}
                >
                  {posterUrl && (
                    <img src={posterUrl} alt="Video poster" className={styles.posterImage} />
                  )}
                  <div className={styles.playButton}>
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="24" fill="rgba(0, 0, 0, 0.5)"/>
                      <path d="M18 14L34 24L18 34V14Z" fill="#ffffff"/>
                    </svg>
                  </div>
                </div>
              )}
              {/* {!showPoster && (
                <button 
                  className={styles.stopButton}
                  onClick={handleStopClick}
                  aria-label="Stop video"
                >
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="rgba(0, 0, 0, 0.5)"/>
                    <rect x="16" y="16" width="16" height="16" rx="2" fill="#ffffff"/>
                  </svg>
                </button>
              )} */}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroVideo;

